// The `GolfInsurance` component is a React functional component that displays detailed information about a
// golf insurance plan. It takes `golfInsurance` data as a prop and renders various sections such as a hero
// banner, discount information, summary details, rubber info, schedule details, FAQs, and terms & conditions.
// The component utilizes Tailwind CSS for styling and includes elements like images, markdown descriptions,
// links, and an accordion for FAQs. It also highlights discounts dynamically by formatting percentage-based
// offers. Additionally, it integrates an embedded YouTube video and call-to-action buttons for user interaction.

import React, { useState } from 'react';
import { GolfInsuranceData, GolfInsuranceFAQ } from 'types/next';
import { MarkDownDescription } from 'components/commercetools-ui/atoms/markdown/markdown';
import Link from 'components/commercetools-ui/atoms/link';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';

interface GolfInsuranceProps {
  golfInsurance: GolfInsuranceData;
}

const GolfInsurance = ({ golfInsurance }: GolfInsuranceProps) => {
  const heroBanner = golfInsurance?.heroBanner || '';
  const heroMobileImage = golfInsurance?.heroBanner?.heroMobileImage || '';
  const discountInfo = golfInsurance?.discountInfo || '';
  const rubberInfo = golfInsurance?.rubberInfo || '';
  const scheduleinfo = golfInsurance?.scheduleInfo || '';
  const summaryDetails = golfInsurance?.summaryDetails || '';
  const termsAndCondition = golfInsurance?.termsAndConditions || '';
  const exclusiveText = golfInsurance?.exclusiveText || '';
  const faqs = golfInsurance?.faqs || '';

  const [showAll, setShowAll] = useState(false);
  const visibleItems = showAll ? (
    <MarkDownDescription
      markdownText={discountInfo?.description}
      classname="text-center text-18 font-normal link-green leading-9 md:hidden"
    />
  ) : (
    <MarkDownDescription
      markdownText={discountInfo?.description.slice(0, 145)}
      classname="text-center text-18 font-normal link-green  leading-9 md:hidden"
    />
  );

  const formatDiscountText = (text: string) => {
    const regex = /(\d+%\s*off)/i;
    const match = text.match(regex);

    if (match) {
      const [fullMatch] = match;
      const splitText = text.split(fullMatch);
      return (
        <>
          {splitText[0]}
          <span className="font-bold text-red-500">{fullMatch}</span>
          {splitText[1]}
        </>
      );
    }

    return text;
  };
  return (
    <>
      <div className="hero-section mb-20 ">
        <img
          src={`https://americangolf.a.bigcontent.io/v1/static/${heroBanner?.heroImage?.name}`}
          className="hidden w-[100%] md:block "
        />
        <img
          src={`https://americangolf.a.bigcontent.io/v1/static/${heroMobileImage?.name}`}
          className=" w-[100%] md:hidden  "
        />
      </div>
      <div className="discount-info-section mb-20 flex flex-col items-center justify-center px-15 py-20 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        <div className="text-36">
          <h3 className="my-20 px-5 text-center  text-36 font-semibold leading-8 text-red-500 md:hidden">
            {exclusiveText}
          </h3>
          <h3 className="mb-10 text-center font-body text-36 font-bold text-[#1A1871]">
            {formatDiscountText(discountInfo?.overallTitle)}
          </h3>
        </div>
        <div className="m-auto items-center justify-center p-15 text-center">
          <MarkDownDescription
            classname="hidden text-18 font-normal leading-9 md:block"
            markdownText={discountInfo?.description}
          />
          {visibleItems}
          <button
            className=" text-15  md:hidden"
            onClick={() => {
              setShowAll(!showAll);
            }}
          >
            {showAll ? (
              <div className="flex items-center">
                <p>READ LESS </p> <p className="ml-2 text-30 text-green-500">-</p>
              </div>
            ) : (
              <div className="flex items-center">
                {' '}
                <p>READ MORE </p>
                <p className="ml-2 text-30 text-green-500 ">+</p>
              </div>
            )}
          </button>
        </div>
      </div>
      {/* Summary Details Section */}
      <div className="summary-details-section mb-20 grid grid-cols-1 gap-8 p-6 md:w-full lg:mx-auto lg:w-[1000px] lg:grid-cols-2 2xl:w-[73rem]">
        {/* Left Section - Summary Details */}
        <div className="flex min-h-[550px] flex-col justify-center">
          <div className="flex h-50  items-center bg-[#1A1871] p-4">
            <h3 className=" text-left  font-body text-21 font-bold text-white">{summaryDetails?.summaryTitle}</h3>
          </div>

          <div className="bg-gray-200 ">
            <div className="p-10">
              {summaryDetails?.cover_details.map((detail, index) => (
                <div key={index} className="mb-5  flex justify-between px-8 text-left font-body text-16 font-medium">
                  <span className="my-10 ml-10 block font-body text-20  leading-5">
                    {<MarkDownDescription markdownText={detail.name} classname="font-normal summary-details" />}{' '}
                  </span>
                  <span className="my-10 mr-10 block font-body text-20  leading-5">
                    <MarkDownDescription markdownText={detail.value} classname="font-normal summary-details" />
                  </span>
                </div>
              ))}

              {summaryDetails?.additional_benefits.map((benefit, index) => (
                <div
                  key={index + summaryDetails?.cover_details.length}
                  className="mb-5 flex justify-between px-8 text-left font-body text-16 font-medium"
                >
                  <span className="my-10 ml-10 block font-body text-20 font-normal leading-5">{benefit}</span>
                  <span className="my-10 mr-10 block font-body text-20 font-normal leading-5">✓</span>
                </div>
              ))}

              <div className="flex min-h-[82px] items-center justify-center px-35 py-25">
                <Link
                  link={summaryDetails?.full_details.url}
                  className="text-blue-500 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {summaryDetails?.full_details.text}
                </Link>
              </div>
            </div>
            {/* Pricing Section */}
            <div className="pricing-section flex min-h-[56px] items-center justify-center bg-white px-1">
              <p className="mb-4 font-body text-20 font-medium text-gray-700">
                {summaryDetails?.pricing.discount_text}
                <span className="mb-2 ml-2 font-body text-20 font-medium text-gray-700 line-through">
                  {summaryDetails?.pricing.original_price}
                </span>
                <span className="mb-4 ml-2 font-body text-20 font-bold text-red-500">
                  {summaryDetails?.pricing.discounted_price}
                </span>
              </p>
            </div>
            <div className="flex h-[75px] w-full items-center justify-center bg-[#CD1544]">
              <Link
                link={summaryDetails?.cta.url}
                className="px-6 py-2 font-body text-20 font-semibold text-white"
                target="_blank"
                rel="noopener noreferrer"
              >
                {summaryDetails?.cta.text}
              </Link>
            </div>
          </div>
        </div>

        {/* Right Section - Image */}
        <div className="flex  items-center justify-center">
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${summaryDetails?.heroImage?.name}`}
            className="md:w-sm w-sm block object-cover md:hidden lg:block"
          />
        </div>
      </div>
      <div className="rubberring-section  mb-50 grid grid-cols-12 gap-4 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        <div className="col-span-12 p-4  md:col-span-2">
          <div className="hidden  max-w-[150px] md:block">
            <img
              src={`https://americangolf.a.bigcontent.io/v1/static/${rubberInfo?.image?.name}`}
              className="sm:none w-full"
              alt="Rubber Info"
            />
          </div>
        </div>
        <div className="col-span-12 flex items-center p-20   md:col-span-10">
          <MarkDownDescription
            markdownText={rubberInfo?.description}
            classname="text-lg/7 rubberInfo text-center link-blue link-no-underline lg:text-left text-wrap font-light lg:align-baseline"
          />
        </div>
      </div>
      <div className="sm:mb-20 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        <iframe
          className="aspect-video h-[453px] w-full sm:mb-10"
          src={`${rubberInfo?.youtube ?? 'https://www.youtube.com/embed/BlJu9hj5N1E'}`}
          title="YouTube video"
          allowFullScreen
        ></iframe>
        <div className="m-0 flex  h-[75px] w-full  items-center justify-center ">
          <Link link={rubberInfo?.cta?.link} className="w-full sm:w-[322px]">
            <button className="h-[72px] w-full bg-[#e40036] font-body text-16 uppercase leading-4 text-white sm:w-[322px] ">
              {rubberInfo?.cta?.text}
            </button>
          </Link>
        </div>
      </div>
      <div className="schedule-section mb-30 grid grid-cols-12 gap-4 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        <div className="col-span-12 p-4 md:col-span-7">
          <h3 className="mb-20 text-center font-body text-3xl font-bold text-[#272B5c] md:text-left">
            {scheduleinfo?.overallTitle}
          </h3>
          <MarkDownDescription
            classname="mb-20 font-body text-18 font-normal leading-8"
            markdownText={scheduleinfo?.description}
          />
          <Link link={scheduleinfo?.cta?.link}>
            <button className="hover:border-1 h-[72px] w-full bg-[#1b1b3c] font-body text-16 uppercase leading-4 text-white hover:border-[#1b1b3c] hover:bg-white hover:text-[#1b1b3c] md:w-[322px]">
              {scheduleinfo?.cta?.text}
            </button>
          </Link>
        </div>
        <div className="col-span-12 flex items-center  justify-center p-4 md:col-span-5">
          <div className="mx-17 hidden h-[367px] w-full bg-[#f6f6f6] p-15 md:block lg:block">
            <div className="max-w-[205px]">
              <img
                src={`https://americangolf.a.bigcontent.io/v1/static/${scheduleinfo?.image?.name}`}
                className="h-[100%] w-[100%] items-center justify-center"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="faq-section mb-20 p-10 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        {faqs &&
          faqs?.content?.map((faq: GolfInsuranceFAQ, index: number) => {
            return (
              <AccordionBtn
                variant="plusAndMinus"
                className="items-center border-b border-[#d1c9c4] py-15 text-16   leading-8"
                closedSectionTitle={faq?.question}
                sectionTileClassName={'font-semibold text-18'}
                panelClassName="text-primary-black  pt-10"
                key={index}
                iconClassName="text-black"
              >
                <div className="!font-normal">{faq.answer}</div>
              </AccordionBtn>
            );
          })}
      </div>
      <div className="terms-and-condition mb-20 p-10 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        <h3 className="mb-30 text-center text-36 font-bold leading-7 text-[#272B5c] md:text-left">
          {termsAndCondition?.overallTitle}
        </h3>
        <div className="text-center md:text-left">
          {termsAndCondition?.content &&
            termsAndCondition?.content.map((item, index: number) => (
              <>
                <MarkDownDescription
                  key={index}
                  classname="mb-4 rubberInfo font-body text-16 font-normal leading-8 "
                  markdownText={item?.paragraph}
                />
                <br />
              </>
            ))}
        </div>
      </div>
    </>
  );
};

export default GolfInsurance;
